import * as React from 'react';
import { detect } from 'detect-browser';
import { Button, Dropdown, Menu, Typography } from 'antd';
import { SettingOutlined, KeyOutlined, UndoOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import './HelpSegment.css';
import KeyboardUtils, { KeyboardCommand, KeyboardShortcuts } from '../keyboard-utils';

const { Text } = Typography;
const browser = detect();

const getCommandModifier = () => (browser && browser.os === 'Mac OS' ? '⌘' : 'Ctrl');

interface Props {
  keyboardUtils: KeyboardUtils;
}

interface State {
  isOpen: boolean;
  settingFor: string;
  shortcuts: KeyboardShortcuts;
}

export default class HelpSegment extends React.Component<Props, State> {
  private timeout?: any;
  constructor(props: Props) {
    super(props);

    this.state = {
      isOpen: false,
      settingFor: '',
      shortcuts: this.props.keyboardUtils.getShortcuts(),
    };
  }

  componentWillUnmount = () => {
    this.timeout && clearTimeout(this.timeout);
  };

  getCommandKey = (key: string): string => {
    switch (key) {
      case ' ':
        return 'Bilstöng';
      case 'ArrowLeft':
        return 'Vinstri';
      case 'ArrowRight':
        return 'Hægri';
      case 'ArrowDown':
        return 'Niður';
      case 'ArrowUp':
        return 'Upp';
      default:
        return key;
    }
  };

  getCommandHelpMessage = (command: string): string => {
    switch (command) {
      case 'record':
        return 'Hefja / stöðva upptöku';
      case 'download':
        return 'Afrita allan textann';
      case 'save':
        return 'Ljúka og vista';
      case 'jump':
        return 'Hoppa á þann stað í upptöku sem textabendill vísar';
      case 'restart':
        return 'Opna nýtt';
      case 'seekBack':
        return 'Hoppa 2 sekúndur aftur';
      case 'seekForward':
        return 'Hoppa 2 sekúndur fram';
      case 'playback':
        return 'Spila / stöðva hljóð (þegar upptaka er ekki í gangi)';
      case 'capitalize':
        return 'Breyta upphafsstaf orðs undir textabendli í hástaf';
      case 'lowercase':
        return 'Breyta upphafsstaf orðs undir textabendli í lágstaf';
      case 'labelSpeaker':
        return 'Opna / loka merkja mælanda ham';
      default:
        return '';
    }
  };

  onSetShortcut = (key: string) => {
    const { keyboardUtils } = this.props;
    this.setState({
      settingFor: key,
    });
    keyboardUtils.setShortcut(key as KeyboardCommand, () => {
      const shortcuts = keyboardUtils.getShortcuts();
      this.setState({
        shortcuts,
        settingFor: '',
      });
    });
  };

  onReset = () => {
    const { keyboardUtils } = this.props;
    keyboardUtils.resetDefaults();
    const shortcuts = keyboardUtils.getShortcuts();
    this.setState({ settingFor: '', shortcuts: { ...shortcuts } });
  };

  handleOpenChange = (flag: boolean) => {
    if (!!this.state.settingFor && !flag) {
      return;
    }
    this.setState({ isOpen: flag });
  };

  handleCloseWithoutChanges = () => {
    this.setState({
      settingFor: '',
      isOpen: false,
    });
  };

  render() {
    const { isOpen, settingFor, shortcuts } = this.state;
    const isSetting = !!settingFor;

    const menu = (
      <Menu selectedKeys={[settingFor]}>
        <Menu.ItemGroup
          title={
            <>
              <KeyOutlined /> <strong>Flýtilyklar</strong>
            </>
          }
        >
          <Menu.Item disabled>Smelltu á lykil til þess að breyta honum</Menu.Item>
        </Menu.ItemGroup>
        <Menu.Divider />
        {Object.keys(shortcuts).map((key: string) => {
          const shortcut = shortcuts[key];
          const shortcutKey = `${shortcut.hasCommandModifier ? `${getCommandModifier()} + ` : ''}${this.getCommandKey(
            shortcut.key,
          )}`;

          return (
            <Menu.Item key={key} onClick={() => this.onSetShortcut(key)}>
              <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>{this.getCommandHelpMessage(key)}</span>
                <Text type='secondary'>{shortcutKey}</Text>
              </span>
            </Menu.Item>
          );
        })}
        <Menu.Divider />
        <Menu.Item icon={<UndoOutlined />} onClick={this.onReset}>
          Endurstilla flýtilykla
        </Menu.Item>
        <Menu.Item icon={<FullscreenExitOutlined />} onClick={this.handleCloseWithoutChanges}>
          Loka án breytinga
        </Menu.Item>
      </Menu>
    );

    return (
      <Dropdown overlay={menu} open={isOpen || isSetting} onOpenChange={this.handleOpenChange}>
        <Button icon={<SettingOutlined />}>Flýtilyklar</Button>
      </Dropdown>
    );
  }
}
